import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import Button from '../components/Button'
import Icon from '../components/Icon'
import styles from './Login.module.css'
import mfaStyles from './Mfa.module.css'
import { startMfaEnroll, verifyMfaEnroll, verifyMfa, resendMfaEmail, isAuthenticated } from '../api/security'

const logo = (
  <div className={styles.logoRow}>
    <img src="/GaussIQlogo.png" alt="GaussIQ Logo" className={styles.logoImage} />
    <div className={styles.logoText}>
      <span className={styles.logoName}>GaussIQ</span>
      <span className={styles.logoTagline}>
        Know your customer<br />Grow your future.
      </span>
    </div>
  </div>
)

/**
 * Mfa — the post-password MFA challenge, reached only via login()'s
 * { mfaRequired: true, stage, method, pendingToken } result (see
 * api/security.js). `stage` is 'enroll' (first time — no method chosen yet)
 * or 'verify' (already enrolled, method known). Everything here runs on the
 * short-lived pendingToken, not a real session — nothing is persisted client
 * -side until verifyMfa()/verifyMfaEnroll() succeeds and navigate('/dashboard')
 * fires, same as a normal login().
 */
export default function Mfa() {
  const navigate = useNavigate()
  const { state } = useLocation()
  const pendingToken = state?.pendingToken

  // Captured ONCE at mount, not recomputed on every render: verifyMfa/
  // verifyMfaEnroll set the session cookie the moment a code checks out (see
  // _completeLogin in api/security.js) — for a successful TOTP enrollment
  // specifically, that happens *before* this component switches to the
  // backup-codes screen (setPhase('backup-codes') below), all within the same
  // mount. Calling isAuthenticated() fresh on every render (as this used to)
  // meant that very render — the one meant to show backup codes — saw
  // "already authenticated" and rendered nothing: a white screen, since nothing
  // re-runs the redirect effect for a plain client-side cookie change (its
  // deps don't change), so the correct navigate('/dashboard') never fired
  // until a full remount (i.e. refresh) re-evaluated everything from scratch.
  // A value captured once at mount doesn't have that problem: it only ever
  // reflects whether the user already had a session *before* this page ran,
  // which is the actual case this guards against — reaching /mfa some other
  // way (stale refresh, bookmark) while already logged in, not the normal
  // in-flow moment enrollment itself completes.
  const [alreadyAuthenticated] = useState(isAuthenticated)

  useEffect(() => {
    if (alreadyAuthenticated) {
      navigate('/dashboard', { replace: true })
    } else if (!pendingToken) {
      navigate('/login', { replace: true })
    }
  }, [alreadyAuthenticated, pendingToken, navigate])

  // 'method-pick' (enroll, no method chosen) -> 'enroll-code' (TOTP/email code
  // entry to confirm enrollment) -> 'backup-codes' (TOTP only, shown once).
  // 'verify' skips straight to code entry for the already-known method.
  const [phase, setPhase]           = useState(state?.stage === 'enroll' ? 'method-pick' : 'verify-code')
  const [method, setMethod]         = useState(state?.method || null)
  const [secretBase32, setSecret]   = useState(null)
  const [otpauthUri, setOtpauthUri] = useState(null)
  const [code, setCode]             = useState('')
  const [useBackupCode, setUseBackupCode] = useState(false)
  const [trustDevice, setTrustDevice]     = useState(false)
  const [backupCodes, setBackupCodes]     = useState(null)
  const [busy, setBusy]             = useState(false)
  const [error, setError]           = useState('')
  const [notice, setNotice]         = useState('')

  if (!pendingToken || alreadyAuthenticated) return null

  function backToLogin() {
    navigate('/login', { replace: true })
  }

  async function chooseMethod(chosen) {
    setError('')
    setBusy(true)
    try {
      const result = await startMfaEnroll(pendingToken, chosen)
      setMethod(chosen)
      if (chosen === 'totp') {
        setSecret(result.secret_base32)
        setOtpauthUri(result.otpauth_uri)
      } else {
        setNotice('We sent a 6-digit code to your email.')
      }
      setPhase('enroll-code')
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  async function submitEnrollCode(e) {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      const result = await verifyMfaEnroll(pendingToken, code, trustDevice)
      if (result.backupCodes?.length) {
        setBackupCodes(result.backupCodes)
        setPhase('backup-codes')
      } else {
        navigate('/dashboard')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  async function submitVerifyCode(e) {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      await verifyMfa(pendingToken, code, trustDevice)
      navigate('/dashboard')
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  async function handleResend() {
    setError('')
    setNotice('')
    try {
      await resendMfaEmail(pendingToken)
      setNotice('A new code is on its way.')
    } catch (err) {
      setError(err.message)
    }
  }

  function copyBackupCodes() {
    navigator.clipboard?.writeText(backupCodes.join('\n')).then(
      () => setNotice('Copied to clipboard.'),
      () => setError('Could not copy — select and copy the codes manually.'),
    )
  }

  function downloadBackupCodes() {
    const blob = new Blob([backupCodes.join('\n') + '\n'], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'gaussiq-backup-codes.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── method-pick: enroll, no method chosen yet ──────────────────────────────
  if (phase === 'method-pick') {
    return (
      <div className={styles.pageBackground}>
        <div className={styles.card}>
          {logo}
          <div className={mfaStyles.stack}>
            <p className={styles.pageHeading}>Set up two-factor authentication</p>
            <p className={mfaStyles.subtext}>Your workspace requires a second sign-in step. Choose how you'd like to receive your codes.</p>
            {error && <p className={styles.errorMessage}>{error}</p>}
            <div className={mfaStyles.methodChoices}>
              <button type="button" className={mfaStyles.methodCard} disabled={busy} onClick={() => chooseMethod('totp')}>
                <Icon name="lock" width="22" height="22" />
                <span className={mfaStyles.methodTitle}>Authenticator app</span>
                <span className={mfaStyles.methodDesc}>Google Authenticator, Authy, 1Password, or any TOTP app</span>
              </button>
              <button type="button" className={mfaStyles.methodCard} disabled={busy} onClick={() => chooseMethod('email')}>
                <Icon name="mail" width="22" height="22" />
                <span className={mfaStyles.methodTitle}>Email code</span>
                <span className={mfaStyles.methodDesc}>We'll email you a code at sign-in</span>
              </button>
            </div>
            <div className={styles.links}>
              <button type="button" className={styles.linkButton} onClick={backToLogin}>Back to login</button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ── enroll-code: confirm the first TOTP/email code ─────────────────────────
  if (phase === 'enroll-code') {
    return (
      <div className={styles.pageBackground}>
        <div className={styles.card}>
          {logo}
          <form onSubmit={submitEnrollCode} className={styles.form}>
            <p className={styles.pageHeading}>
              {method === 'totp' ? 'Scan the QR code' : 'Enter your email code'}
            </p>

            {method === 'totp' && (
              <>
                <div className={mfaStyles.qrWrap}>
                  <QRCodeSVG value={otpauthUri} size={180} />
                </div>
                <p className={mfaStyles.subtext}>
                  Scan with your authenticator app, or enter this key manually:
                </p>
                <code className={mfaStyles.manualKey}>{secretBase32}</code>
              </>
            )}

            {notice && <p className={styles.successMessage}>{notice}</p>}
            {error && <p className={styles.errorMessage}>{error}</p>}

            <div className={styles.fieldGroup}>
              <label htmlFor="code" className={styles.label}>
                {method === 'totp' ? '6-digit code from your app' : '6-digit code from your email'}
              </label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                value={code}
                onChange={e => setCode(e.target.value)}
                className={styles.input}
                required
                autoFocus
              />
            </div>

            <label className={mfaStyles.trustRow}>
              <input type="checkbox" checked={trustDevice} onChange={e => setTrustDevice(e.target.checked)} />
              Trust this device
            </label>

            <div className={styles.buttonRow}>
              <Button type="submit" variant="primary" fullWidth disabled={busy || !code}>
                {busy ? 'Verifying…' : 'Verify and continue'}
              </Button>
            </div>

            <div className={styles.links}>
              {method === 'email' && (
                <button type="button" className={styles.linkButton} onClick={handleResend}>Resend code</button>
              )}
              <button type="button" className={styles.linkButton} onClick={backToLogin}>Back to login</button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // ── backup-codes: shown exactly once, right after confirming TOTP ──────────
  if (phase === 'backup-codes') {
    return (
      <div className={styles.pageBackground}>
        <div className={styles.card}>
          {logo}
          <div className={mfaStyles.stack}>
            <p className={styles.pageHeading}>Save your backup codes</p>
            <p className={mfaStyles.subtext}>
              Use one of these if you ever lose access to your authenticator app. Each code
              works once. This is the only time they'll be shown — save them somewhere safe.
            </p>
            {notice && <p className={styles.successMessage}>{notice}</p>}
            {error && <p className={styles.errorMessage}>{error}</p>}
            <div className={mfaStyles.backupCodeGrid}>
              {backupCodes.map(c => <code key={c} className={mfaStyles.backupCode}>{c}</code>)}
            </div>
            <div className={mfaStyles.backupActions}>
              <button type="button" className={mfaStyles.iconButton} onClick={copyBackupCodes}>
                <Icon name="document" width="16" height="16" /> Copy
              </button>
              <button type="button" className={mfaStyles.iconButton} onClick={downloadBackupCodes}>
                <Icon name="download" width="16" height="16" /> Download
              </button>
            </div>
            <div className={styles.buttonRow}>
              <Button type="button" variant="primary" fullWidth onClick={() => navigate('/dashboard')}>
                I've saved my backup codes
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ── verify-code: steady-state challenge for an already-enrolled method ─────
  return (
    <div className={styles.pageBackground}>
      <div className={styles.card}>
        {logo}
        <form onSubmit={submitVerifyCode} className={styles.form}>
          <p className={styles.pageHeading}>Enter your two-factor code</p>

          {notice && <p className={styles.successMessage}>{notice}</p>}
          {error && <p className={styles.errorMessage}>{error}</p>}

          <div className={styles.fieldGroup}>
            <label htmlFor="verifyCode" className={styles.label}>
              {useBackupCode ? 'Backup code' : method === 'email' ? '6-digit code from your email' : '6-digit code from your app'}
            </label>
            <input
              id="verifyCode"
              type="text"
              inputMode={useBackupCode ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              placeholder={useBackupCode ? 'XXXXXXXX' : '123456'}
              value={code}
              onChange={e => setCode(e.target.value)}
              className={styles.input}
              required
              autoFocus
            />
          </div>

          <label className={mfaStyles.trustRow}>
            <input type="checkbox" checked={trustDevice} onChange={e => setTrustDevice(e.target.checked)} />
            Trust this device
          </label>

          <div className={styles.buttonRow}>
            <Button type="submit" variant="primary" fullWidth disabled={busy || !code}>
              {busy ? 'Verifying…' : 'Verify'}
            </Button>
          </div>

          <div className={styles.links}>
            {method === 'totp' && (
              <button type="button" className={styles.linkButton} onClick={() => { setUseBackupCode(v => !v); setCode('') }}>
                {useBackupCode ? 'Use my authenticator app instead' : 'Use a backup code instead'}
              </button>
            )}
            {method === 'email' && (
              <button type="button" className={styles.linkButton} onClick={handleResend}>Resend code</button>
            )}
            <button type="button" className={styles.linkButton} onClick={backToLogin}>Back to login</button>
          </div>
        </form>
      </div>
    </div>
  )
}
